/**
 * Toast when a notification arrives on the multiplexed /ws/signals channel
 * (broadcast as window event `notifications:ws`). Report-ready uses success styling.
 *
 * Never toast when signed out — session expiry / wake-from-sleep can still deliver
 * a WS notification (or leave a live toast) while the user is already on sign-in.
 */
import { useEffect, useRef } from "react";
import { useAuth } from "@clerk/clerk-react";
import { toast } from "sonner";

import { subscribeReportReady } from "../utils/reportReadyEvents";
import { toastReportReadyOnce } from "../utils/reportReadyToast";
import { resolveNotificationDisplayTitle } from "../utils/notificationDisplay";

const EVENT_NOTIFICATIONS_WS = "notifications:ws";

function plainFromMarkdown(md: string, maxLen: number): string {
  if (!md) return "";
  let s = String(md)
    .replace(/\*\*([^*]+)\*\*/g, "$1")
    .replace(/\*([^*]+)\*/g, "$1")
    .replace(/`([^`]+)`/g, "$1")
    .replace(/\[(.*?)\]\([^)]+\)/g, "$1")
    .replace(/#{1,6}\s+/g, "")
    .replace(/\n+/g, " ")
    .trim();
  if (s.length > maxLen) {
    return `${s.slice(0, maxLen - 1)}…`;
  }
  return s;
}

function readReportIdFromNotificationRow(row: Record<string, unknown>): string {
  const dataJson = row.data_json ?? row.dataJson;
  if (!dataJson || typeof dataJson !== "object") return "";
  const reportId = (dataJson as Record<string, unknown>).report_id;
  return typeof reportId === "string" ? reportId.trim() : "";
}

export default function NotificationToastListener() {
  const { isLoaded, isSignedIn } = useAuth();
  const signedInRef = useRef(false);
  signedInRef.current = Boolean(isLoaded && isSignedIn);

  useEffect(() => {
    return subscribeReportReady((detail) => {
      if (!signedInRef.current) return;
      toastReportReadyOnce(detail);
    });
  }, []);

  useEffect(() => {
    const onWsNotification = (evt: Event) => {
      if (!signedInRef.current) return;

      const detail = (evt as CustomEvent).detail as { row?: Record<string, unknown> };
      const row = detail?.row;
      if (!row || row.id == null) return;

      const unread =
        String(row.status_in_app ?? row.statusInApp ?? "unread").toLowerCase() !== "read";
      if (!unread) return;

      const title = resolveNotificationDisplayTitle(
        String(row.title ?? "Notification"),
        row.event_key != null
          ? String(row.event_key)
          : row.eventKey != null
            ? String(row.eventKey)
            : null,
        (row.data_json ?? row.data) as import("../types/notifications").NotificationItem["data"]
      );
      const rawBody = String(row.body_markdown ?? row.bodyMarkdown ?? "");
      const description = plainFromMarkdown(rawBody, 160);
      const eventKey = String(row.event_key ?? row.eventKey ?? "").toLowerCase();
      const category = String(row.category ?? "").toLowerCase();

      if (category === "reports" && (eventKey === "report_ready" || eventKey === "report_failed")) {
        const reportId = readReportIdFromNotificationRow(row);
        const dataJson = row.data_json ?? row.dataJson;
        const pushAction =
          dataJson && typeof dataJson === "object" && typeof (dataJson as Record<string, unknown>).action === "string"
            ? String((dataJson as Record<string, unknown>).action)
            : undefined;
        toastReportReadyOnce(
          {
            reportId,
            status: eventKey === "report_failed" ? "failed" : "published",
            action: pushAction,
          },
          {
            title,
            description: description || undefined,
          }
        );
        return;
      }

      toast.info(title, {
        description: description || undefined,
      });
    };

    window.addEventListener(EVENT_NOTIFICATIONS_WS, onWsNotification as EventListener);
    return () => window.removeEventListener(EVENT_NOTIFICATIONS_WS, onWsNotification as EventListener);
  }, []);

  return null;
}
